package fr.frux.core.designsystem.components

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.semantics.clearAndSetSemantics
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import fr.frux.core.designsystem.theme.fruxColors

private val monthInitials = listOf("J", "F", "M", "A", "M", "J", "J", "A", "S", "O", "N", "D")

/**
 * Frise de saison janvier-décembre : synthétise en un coup d'œil les mois en récolte (données du
 * référentiel espèces, saison nationale — cf. ADR 0006), ceux bientôt mûrs et le mois courant.
 *
 * Les 12 cellules ont un poids égal ; seule leur couleur porte l'information, le libellé détaillé
 * (cf. [harvestPeriodLabel]) reste à la charge de l'appelant. La frise elle-même est annoncée en
 * une phrase unique en accessibilité : énumérer les 12 cellules une à une serait plus bruyant
 * qu'utile pour un lecteur d'écran.
 */
@Composable
fun SeasonStrip(
    inSeasonMonths: Set<Int>,
    currentMonth: Int,
    modifier: Modifier = Modifier,
    soonMonths: Set<Int> = emptySet(),
) {
    val fruxColors = MaterialTheme.fruxColors
    val outlineColor = MaterialTheme.colorScheme.outline
    val onSurfaceVariant = MaterialTheme.colorScheme.onSurfaceVariant

    Row(
        modifier = modifier
            .fillMaxWidth()
            .semantics {
                contentDescription = seasonStripDescription(inSeasonMonths, soonMonths)
            },
        horizontalArrangement = Arrangement.spacedBy(2.dp),
    ) {
        for (month in 1..12) {
            val cellColor = when (month) {
                in inSeasonMonths -> fruxColors.statusAvailable
                in soonMonths -> fruxColors.statusFlowering
                else -> null
            }
            val isCurrent = month == currentMonth
            val borderColor = cellColor ?: outlineColor

            Box(
                modifier = Modifier
                    .weight(1f)
                    .height(28.dp)
                    .clip(RoundedCornerShape(6.dp))
                    .background(cellColor?.copy(alpha = 0.18f) ?: Color.Transparent)
                    .thenCurrentMonthBorder(isCurrent, borderColor)
                    .clearAndSetSemantics { },
                contentAlignment = Alignment.Center,
            ) {
                Text(
                    text = monthInitials[month - 1],
                    style = MaterialTheme.typography.labelSmall,
                    fontWeight = if (isCurrent) FontWeight.Bold else FontWeight.SemiBold,
                    color = cellColor ?: onSurfaceVariant,
                )
            }
        }
    }
}

/** Bordure du mois courant, dans la couleur de sa propre cellule (statut ou neutre). */
private fun Modifier.thenCurrentMonthBorder(isCurrent: Boolean, color: Color): Modifier =
    if (isCurrent) {
        this.border(width = 2.dp, color = color, shape = RoundedCornerShape(6.dp))
    } else {
        this
    }

/**
 * Résume la frise en une phrase française pour l'accessibilité, ex. "En récolte de septembre à
 * octobre, bientôt mûr en août" ou "Hors saison toute l'année" si aucun mois n'est renseigné.
 */
private fun seasonStripDescription(inSeasonMonths: Set<Int>, soonMonths: Set<Int>): String {
    if (inSeasonMonths.isEmpty() && soonMonths.isEmpty()) return "Hors saison toute l'année"
    val parts = mutableListOf<String>()
    if (inSeasonMonths.isNotEmpty()) {
        parts += "En récolte ${monthsRangePhrase(inSeasonMonths)}"
    }
    if (soonMonths.isNotEmpty()) {
        parts += "bientôt mûr ${monthsRangePhrase(soonMonths)}"
    }
    return parts.joinToString(", ").replaceFirstChar { it.uppercase() }
}

/** Construit "en <mois>" ou "de <mois> à <mois>" à partir d'un ensemble de mois non vide. */
private fun monthsRangePhrase(months: Set<Int>): String {
    val start = months.min()
    val end = months.max()
    return if (start == end) {
        "en ${monthLongFr(start)}"
    } else {
        "de ${monthLongFr(start)} à ${monthLongFr(end)}"
    }
}

private val monthNamesLong = listOf(
    "janvier", "février", "mars", "avril", "mai", "juin",
    "juillet", "août", "septembre", "octobre", "novembre", "décembre",
)

private fun monthLongFr(month: Int): String = monthNamesLong[month - 1]
